import React, { useEffect } from "react";
import { Create, useForm, useSelect } from "@refinedev/antd";
import { Card, Col, DatePicker, Form, Input, InputNumber, Row, Select, Switch } from "antd";
import dayjs from "dayjs";

// Define interface to match the server model exactly
interface PriceModel {
    id: number;
    itemCode: string;
    inventoryItemId: string;
    basePrice: number;
    discountType: string;
    discountValue: number;
    minimumQuantity: number;
    maximumQuantity: number;
    effectivePrice: number;
    startDate: string;
    endDate?: string;
    isActive: boolean;
    currencyCode: string;
    createdAt?: string;
    updatedAt?: string;
}

export const PriceListCreate: React.FC = () => {
    const { form, formProps, saveButtonProps } = useForm({
        resource: "price",
        meta: {
            fields: [
                "id",
                "itemCode",
                "inventoryItemId",
                "basePrice",
                "discountType",
                "discountValue",
                "minimumQuantity",
                "maximumQuantity",
                "effectivePrice",
                "startDate",
                "endDate",
                "isActive",
                "currencyCode",
                "createdAt",
                "updatedAt"
            ]
        },
        onMutationSuccess: (data: PriceModel) => {
            console.log('Price created:', data);
        },
        onMutationError: (error: Error) => {
            console.error('Error creating price:', error);
        }
    });

    // Get currencies for dropdown
    const { selectProps: currencySelectProps } = useSelect({
        resource: "currency",
        optionLabel: "name",
        optionValue: "currencyCode",
    });

    // Get items for autocomplete
    const { selectProps: itemSelectProps, queryResult: itemQueryResult } = useSelect({
        resource: "inventory",
        optionLabel: "itemName",
        optionValue: "itemCode",
        meta: {
            fields: [
                "id",
                "itemCode",
                "itemName",
                "itemClassificationCode",
                "defaultUnitPrice"
            ]
        }
    });

    useEffect(() => {
        // Debug: Log inventory data when it changes
        console.log("Inventory items loaded:", itemQueryResult?.data?.data);
    }, [itemQueryResult?.data?.data]);

    useEffect(() => {
        // Set default values
        form.setFieldsValue({
            id: 0,
            isActive: true,
            startDate: dayjs().format(),
            discountType: "None",
            discountValue: 0,
            minimumQuantity: 1,
            maximumQuantity: 999999,
            effectivePrice: 0,
            basePrice: 0,
            currencyCode: "USD" // Default currency
        });
    }, [form]);

    const handleItemSelect = (value: string) => {
        console.log("Selected item code:", value);
        const itemData = itemQueryResult?.data?.data?.find(
            (item: any) => item.itemCode === value
        );

        console.log("Found item data:", itemData);

        if (itemData) {
            const basePrice = itemData.defaultUnitPrice || 0;

            console.log("Setting inventoryItemId to:", itemData.id);

            // Set both the ID and store the item code for reference
            form.setFieldsValue({
                inventoryItemId: itemData.id,
                selectedItemCode: value,
                basePrice: basePrice,
                effectivePrice: basePrice
            });

            updateEffectivePrice();
        }
    };

    const updateEffectivePrice = () => {
        const basePrice = form.getFieldValue('basePrice') || 0;
        const discountType = form.getFieldValue('discountType');
        const discountValue = form.getFieldValue('discountValue') || 0;

        let effectivePrice = basePrice;

        if (discountType === "Percentage") {
            effectivePrice = basePrice - (basePrice * (discountValue / 100));
        } else if (discountType === "FixedAmount") {
            effectivePrice = basePrice - discountValue;
        }

        // Ensure effective price is not negative
        effectivePrice = Math.max(0, effectivePrice);

        form.setFieldsValue({
            effectivePrice
        });
    };

    const handleBasePriceChange = () => {
        updateEffectivePrice();
    };

    const handleDiscountTypeChange = () => {
        updateEffectivePrice();
    };

    const handleDiscountValueChange = () => {
        updateEffectivePrice();
    };

    return (
        <Create saveButtonProps={saveButtonProps}>
            <Form {...formProps} layout="vertical">
                <Card title="Price Details">
                    <Row gutter={16}>
                        <Col xs={24}>
                            <Form.Item
                                name="inventoryItemId"
                                hidden
                            >
                                <Input id="inventoryItemId" />
                            </Form.Item>

                            {/* This is the display field for selecting the item */}
                            <Form.Item
                                label="Item Code"
                                name="itemCode"
                                rules={[{ required: true, message: 'Missing item code' }]}
                            >
                                <Select
                                    {...itemSelectProps}
                                    showSearch
                                    placeholder="Search item"
                                    filterOption={false}
                                    onChange={(value) => handleItemSelect(value)}
                                    style={{ width: '200px' }}
                                />
                            </Form.Item>

                            {/* Hidden field to store selected item code */}
                            <Form.Item name="selectedItemCode" hidden>
                                <Input id="selectedItemCode" />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="basePrice"
                                label="Base Price"
                                rules={[{ required: true, message: 'Base price is required' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={999999999}
                                    precision={2}
                                    onChange={handleBasePriceChange}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="discountType"
                                label="Discount Type"
                                rules={[{ required: true, message: 'Discount type is required' }]}
                            >
                                <Select
                                    onChange={handleDiscountTypeChange}
                                    style={{ width: "100%" }}
                                    options={[
                                        { label: "None", value: "None" },
                                        { label: "Percentage", value: "Percentage" },
                                        { label: "Fixed Amount", value: "FixedAmount" }
                                    ]}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="discountValue"
                                label="Discount Value"
                                rules={[{ required: true, message: 'Discount value is required' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={999999999}
                                    precision={2}
                                    onChange={handleDiscountValueChange}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="effectivePrice"
                                label="Effective Price"
                            >
                                <InputNumber
                                    disabled
                                    min={0}
                                    max={999999999}
                                    precision={2}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="minimumQuantity"
                                label="Min Quantity"
                                rules={[{ required: true, message: 'Minimum quantity is required' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={999999999}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="maximumQuantity"
                                label="Max Quantity"
                                rules={[{ required: true, message: 'Maximum quantity is required' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={999999999}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="currencyCode"
                                label="Currency"
                                rules={[{ required: true, message: 'Currency is required' }]}
                            >
                                <Select
                                    {...currencySelectProps}
                                    placeholder="Select currency"
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="startDate"
                                label="Start Date"
                                rules={[{ required: true, message: 'Start date is required' }]}
                                getValueProps={(value: any) => ({
                                    value: value ? dayjs(value) : undefined,
                                })}
                            >
                                <DatePicker style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="endDate"
                                label="End Date"
                                getValueProps={(value: any) => ({
                                    value: value ? dayjs(value) : undefined,
                                })}
                            >
                                <DatePicker style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="isActive"
                                label="Active"
                                valuePropName="checked"
                                initialValue={true}
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>
            </Form>
        </Create>
    );
}; 